import { CognitoUserPool, CognitoUser, CognitoUserSession, CognitoRefreshToken } from 'amazon-cognito-identity-js';

// Keys for sessionStorage
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'vitl_access_token',
  ID_TOKEN: 'vitl_id_token',
  REFRESH_TOKEN: 'vitl_refresh_token',
  TOKEN_EXPIRY: 'vitl_token_expiry',
  USER_EMAIL: 'vitl_user_email'
} as const;

interface StoredTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
  userEmail?: string;
}

class AuthService {
  private static instance: AuthService;
  private userPool: CognitoUserPool | null = null;
  private refreshPromise: Promise<StoredTokens> | null = null;

  private constructor() {
    // Singleton pattern
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Initialize the Cognito user pool
   */
  public setupUserPool(poolConfig: { userPoolId: string; clientId: string; endpoint?: string }): void {
    const poolData = {
      UserPoolId: poolConfig.userPoolId,
      ClientId: poolConfig.clientId,
      ...(poolConfig.endpoint && { endpoint: poolConfig.endpoint })
    };
    this.userPool = new CognitoUserPool(poolData);
  }

  /**
   * Store tokens in sessionStorage after successful login
   */
  public storeTokens(session: CognitoUserSession, userEmail?: string): void {
    const accessToken = session.getAccessToken();
    const idToken = session.getIdToken();
    const refreshToken = session.getRefreshToken();

    // Store in sessionStorage (clears when tab closes)
    sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken.getJwtToken());
    sessionStorage.setItem(STORAGE_KEYS.ID_TOKEN, idToken.getJwtToken());
    sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken.getToken());
    sessionStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, String(accessToken.getExpiration() * 1000));
    
    if (userEmail) {
      sessionStorage.setItem(STORAGE_KEYS.USER_EMAIL, userEmail);
    }
  }

  /**
   * Get stored tokens from sessionStorage
   */
  private getStoredTokens(): StoredTokens | null {
    const accessToken = sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const idToken = sessionStorage.getItem(STORAGE_KEYS.ID_TOKEN);
    const refreshToken = sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    const expiresAt = sessionStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
    const userEmail = sessionStorage.getItem(STORAGE_KEYS.USER_EMAIL);

    if (!accessToken || !idToken || !refreshToken || !expiresAt) {
      return null;
    }

    return {
      accessToken,
      idToken,
      refreshToken,
      expiresAt: parseInt(expiresAt, 10),
      userEmail: userEmail || undefined
    };
  }

  /**
   * Get a valid access token, automatically refreshing if needed
   */
  public async getValidAccessToken(): Promise<string> {
    const tokens = this.getStoredTokens();
    
    if (!tokens) {
      throw new Error('No authentication session found. Please log in.');
    }

    // Check if token is expired or about to expire (5 minute buffer)
    const now = Date.now();
    const fiveMinutesFromNow = now + (5 * 60 * 1000);
    
    if (fiveMinutesFromNow >= tokens.expiresAt) {
      console.log('Token expired or expiring soon, refreshing...');
      const newTokens = await this.refreshAccessToken();
      return newTokens.accessToken;
    }

    return tokens.accessToken;
  }

  /**
   * Refresh the access token using the refresh token
   */
  private async refreshAccessToken(): Promise<StoredTokens> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh using Cognito
   */
  private async performTokenRefresh(): Promise<StoredTokens> {
    const tokens = this.getStoredTokens();
    
    if (!tokens || !this.userPool) {
      throw new Error('Cannot refresh: no tokens or user pool available');
    }

    return new Promise((resolve, reject) => {
      // Recreate the Cognito user
      const userData = {
        Username: tokens.userEmail || '',
        Pool: this.userPool!
      };
      const cognitoUser = new CognitoUser(userData);
      
      // Create refresh token object
      const refreshToken = new CognitoRefreshToken({ 
        RefreshToken: tokens.refreshToken 
      });
      
      // Refresh the session
      cognitoUser.refreshSession(refreshToken, (err, session) => {
        if (err) {
          console.error('Token refresh failed:', err);
          this.clearTokens();
          reject(new Error('Session expired. Please log in again.'));
        } else {
          // Store the new tokens
          this.storeTokens(session, tokens.userEmail);
          
          const newTokens = this.getStoredTokens()!;
          console.log('Token refreshed successfully');
          resolve(newTokens);
        }
      });
    });
  }

  /**
   * Get the current access token without refresh
   */
  public getAccessToken(): string | null {
    const tokens = this.getStoredTokens();
    return tokens?.accessToken || null;
  }

  /**
   * Get the current ID token
   */
  public getIdToken(): string | null {
    const tokens = this.getStoredTokens();
    return tokens?.idToken || null;
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    const tokens = this.getStoredTokens();
    if (!tokens) return false;
    
    // Check if token is not expired
    const now = Date.now();
    return now < tokens.expiresAt;
  }

  /**
   * Clear all tokens from sessionStorage
   */
  public clearTokens(): void {
    // Clear sessionStorage
    Object.values(STORAGE_KEYS).forEach(key => {
      sessionStorage.removeItem(key);
    });

    // Also clear localStorage (migrate away from it)
    localStorage.removeItem('authToken');
    
    // Sign out from Cognito if user pool exists
    if (this.userPool) {
      const cognitoUser = this.userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
    }
  }

  /**
   * Logout the user
   */
  public logout(): void {
    this.clearTokens();
  }

  /**
   * Handle 401 errors by attempting to refresh the token
   */
  public async handle401(): Promise<boolean> {
    try {
      await this.refreshAccessToken();
      return true; // Successfully refreshed
    } catch (error) {
      console.error('Failed to handle 401:', error);
      return false; // Need to re-login
    }
  }

  /**
   * Get user email from stored session
   */
  public getUserEmail(): string | null {
    return sessionStorage.getItem(STORAGE_KEYS.USER_EMAIL);
  }
}

// Export singleton instance
const authService = AuthService.getInstance();
export default authService;
export { AuthService, type StoredTokens };